import { Navigate, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { useDialers } from '@/features/dialers/api';
import { useCreateGroup } from '../api';
import { buildDialerRowsPayload } from '../payload';
import { groupFormSchema, type GroupFormInput } from '../schema';
import { GroupForm } from './GroupForm';
import { useModuleFilter } from '@/lib/permissions/use-module-filter';
import { MODULE_LISTING_KEYS } from '@/lib/constants/modules';
import { useAuthStore } from '@/lib/auth/store';
import { BASE_PERMISSION } from '@/lib/constants/modules';
import { usePermission } from '@/lib/permissions/use-permission';

export function CreateGroupPage() {
  const navigate = useNavigate();
  const dialers = useDialers();
  const create = useCreateGroup();
  const filteredDialers = useModuleFilter(dialers.data ?? [], MODULE_LISTING_KEYS.MANAGE_DIALERS, (d) => d.dialerId);
  const loggedInUserId = Number(useAuthStore((s) => s.userId));
  const canCreate = usePermission('Manage Groups.Create Group', BASE_PERMISSION.ADD);

  const options = filteredDialers.map((d) => ({
    value: String(d.dialerId),
    label: d.dialerName,
  }));
  const form = useForm<GroupFormInput>({
    resolver: zodResolver(groupFormSchema),
    mode: 'onTouched',
    defaultValues: { groupName: '', dialerIds: [] },
  });
  const onSubmit = (input: GroupFormInput) => {
    create.mutate(
      {
        groupName: input.groupName.trim(),
        dialerRows: buildDialerRowsPayload(input.dialerIds, options),
        userId: loggedInUserId,
      },
      {
        onSuccess: (res) => {
          toast.success(res?.message ?? 'Group created');
          navigate('/group-list');
        },
        onError: (err) => toast.error(readErrorMessage(err) ?? 'Could not create the group.'),
      },
    );
  };

  if (!canCreate) return <Navigate to="/group-list" replace />;

  return (
    <GroupForm
      mode="create"
      form={form}
      onSubmit={onSubmit}
      isPending={create.isPending}
      pageTitle="Create Group"
      pageDescription="Logical grouping of dialers used to target campaign delivery."
    />
  );
}

function readErrorMessage(err: unknown): string | undefined {
  if (err && typeof err === 'object') {
    const a = err as { response?: { data?: { message?: string } }; message?: string };
    return a.response?.data?.message ?? a.message;
  }
  return undefined;
}
